import React, { useEffect, useMemo } from 'react';
import { Box, Paper } from '@mui/material';
import { DataGrid, GridColDef, GridFilterModel } from '@mui/x-data-grid';
import { OpportunityQueryData } from '../../../loaders/eventsLoaders';
import { extractIdFromSelf } from '../../../hooks/useNavToEvent';
import { dayliteEventTitleExtractedDateYYYYMMDD } from '../../../utils/appDateFunctions';
import { useNotifications } from '@toolpad/core';

// Define the row type for proper typing
export interface EventRow extends OpportunityQueryData {
  id: string;
}

interface EventsDataGridProps {
  events: OpportunityQueryData[];
  isLoading: boolean;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  onRowClick: (params: any) => void;
}

// Simplified columns with proper typing
const columns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 250,
    filterable: true,
    sortable: true
  },
  {
    field: 'extractedDate',
    headerName: 'Date',
    width: 0,
    filterable: false,
    sortable: true
    // Hide this column as it's just for sorting
  }
];

const EventsDataGrid: React.FC<EventsDataGridProps> = ({
  events,
  isLoading,
  filterModel,
  onFilterModelChange,
  onRowClick
}) => {
  // Prepare rows for the DataGrid with explicit typing and calculated date field
  // Use useMemo to optimize by only recalculating when events change
  const rows = useMemo(
    () =>
      events.map((event) => {
        const id = extractIdFromSelf(event.self);
        const extractedDate = dayliteEventTitleExtractedDateYYYYMMDD(event.name);

        return {
          ...event,
          id,
          extractedDate
        };
      }),
    [events]
  );

  const notifications = useNotifications();
  // Warning notification for large number of events
  useEffect(() => {
    if (rows.length >= 250) {
      notifications.show(
        "Warning: you've reached the max number of events that can be pulled from Daylite at once.  Some results may be missing.  Use the end date filter for accurate results.",
        {
          severity: 'error',
          autoHideDuration: 3000
        }
      );
    }
  }, [rows]);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 200px)', width: '100%', overflow: 'hidden' }}>
      <Paper sx={{ flexGrow: 1, display: 'flex' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          loading={isLoading}
          onRowClick={onRowClick}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          initialState={{
            sorting: {
              sortModel: [{ field: 'extractedDate', sort: 'asc' }]
            },
            columns: {
              columnVisibilityModel: {
                extractedDate: false
              }
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default EventsDataGrid;
